import {
  ModalBackdropStyled,
  ModalCardStyled,
} from '@OnArk/styles/Billing.styled';
import { ButtonStyled } from '@OnArk/styles/Buttons.styled';
import { TooltipStyled } from '@OnArk/styles/Layout.styled';

import React, { FC, ReactElement, useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { mq } from '@hangar31/rc-layout';
import { signalCountsChanged } from '@OnArk/admin/GettingStarted/signalCountsChanged';
import { useSection } from '@OnArk/contexts/section';
import { catalog } from '@onark/observability';
import Icon from '@OnArk/SVGs/Icon';
import { iconOnlyOnMobileCss } from '@OnArk/utils/buttonResponsive';
import posthog from '@OnArk/utils/posthog';
import toColor from '@OnArk/utils/toColor';
import theme from 'theme';

const pulse = keyframes`
  0% {}
  50% {
    box-shadow: 0 0 0 10px ${toColor(theme.colors.clickable, 0)},
      0 0 0 20px ${toColor(theme.colors.clickable, 0)};
  }
  100% {
    box-shadow: 0 0 0 0 ${toColor(theme.colors.clickable, 0)},
      0 0 0 0 ${toColor(theme.colors.clickable, 0)};
  }
`;

const TextStyled = styled.p(
  () => css`
    margin: 0;

    a {
      color: ${theme.colors.white};
      text-decoration: underline;
    }
  `
);

// Inline status flash that lives under the button. Used for both
// "Published" success confirmation and any backend error (gate
// messages, network failures, etc).
const FlashStyled = styled.div<{ tone: 'success' | 'danger' }>(
  ({ theme: t, tone }) => css`
    position: absolute;
    z-index: 10;

    display: flex;
    gap: 0.4rem;
    align-items: center;
    padding: 0.6rem 1rem;

    color: ${tone === 'success' ? t.colors.success : t.colors.warning};
    font-size: 1.3rem;
    font-weight: 500;
    white-space: nowrap;

    background: ${t.colors.white};
    border: 1px solid
      ${tone === 'success' ? t.colors.success : t.colors.warning};
    border-radius: ${t.radius.md};
    box-shadow: ${t.shadows.sm};
  `,
  // Below the md breakpoint the publish button lives in the narrow 5.5rem
  // rail pinned to the bottom-left of the viewport. The desktop anchoring
  // (below the button, right-aligned) would push this nowrap flash off the
  // bottom of the screen and off the left edge. On mobile, sit it above the
  // button and expand rightward into the editor area so it stays visible.
  mq({
    bottom: ['calc(100% + 0.6rem)', undefined, 'auto'],
    left: [0, undefined, 'auto'],
    right: ['auto', undefined, 0],
    top: ['auto', undefined, 'calc(100% + 0.6rem)'],
  })
);

const getWrapper = ({
  needPlan,
  suspended,
}: {
  needPlan: boolean;
  suspended: boolean;
}): FC<React.PropsWithChildren<Record<string, any>>> => {
  if (needPlan) {
    return function NeedPlanToolTip({
      children,
    }: {
      children?: React.ReactNode;
    }): ReactElement {
      return (
        <TooltipStyled
          html={
            <TextStyled>Pick a payment plan to publish your class.</TextStyled>
          }
          position="bottom"
          size="big"
        >
          {children}
        </TooltipStyled>
      );
    };
  }
  if (suspended) {
    return function SuspendedTooltip({
      children,
    }: {
      children?: React.ReactNode;
    }): ReactElement {
      return (
        <TooltipStyled
          html={
            <TextStyled>Reactivate your plan to publish changes.</TextStyled>
          }
          position="bottom"
          size="big"
        >
          {children}
        </TooltipStyled>
      );
    };
  }

  return React.Fragment;
};

type PublishStatus = 'idle' | 'publishing' | 'success';

function PublishBtn() {
  const { activeSubscription, changes, church, client, course, setChanges } =
    useSection();
  const router = useRouter();

  const publishable = activeSubscription;
  const needPlan = !church.customer;
  const suspended = !!church.customer && !activeSubscription;

  const [status, setStatus] = useState<PublishStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const Wrapper = getWrapper({ needPlan, suspended });

  // Reconcile changes count when the count is non-zero — keeps the
  // button accurate after server-side edits that happen outside this
  // page (e.g. a teammate publishing).
  useEffect(() => {
    if (changes) {
      const fn = async () => {
        const { course: c } = await client.getCourseChanges({
          id: course.id,
        });

        setChanges(c?.changed?.length ?? 0);
      };

      void fn();
    }
  }, [changes]);

  // Auto-dismiss success flash.
  useEffect(() => {
    if (status !== 'success') return;
    const t = window.setTimeout(() => setStatus('idle'), 1800);

    return () => window.clearTimeout(t);
  }, [status]);

  // Auto-dismiss error flash.
  useEffect(() => {
    if (!error) return;
    const t = window.setTimeout(() => setError(null), 4000);

    return () => window.clearTimeout(t);
  }, [error]);

  const handleClick = () => {
    if (!changes) return;
    // The billing wall is decided HERE, not by the mutation — these two
    // branches return before publishCourse is ever called, so the server's
    // `classes.course_publish_failed` never sees them. The reasons are spelled
    // the resolver's way so both events answer one query.
    if (needPlan) {
      catalog.emit('classes.course_publish_blocked', {
        churchId: church.id,
        courseId: course.id,
        reason: 'no_payment_method',
      });
      void router.push('/admin/billing/select-plan');

      return;
    }
    if (suspended) {
      catalog.emit('classes.course_publish_blocked', {
        churchId: church.id,
        courseId: course.id,
        reason: 'no_active_subscription',
      });
      void router.push('/admin/billing');

      return;
    }
    posthog?.capture('publishAttempted');
    setConfirming(true);
  };

  const doPublish = async () => {
    setConfirming(false);
    setError(null);
    setStatus('publishing');
    try {
      await client.publishCourse({ id: course.id });
      setChanges(0);
      setStatus('success');
      posthog?.capture('publishedCourse', { courseId: course.id });
      signalCountsChanged();
    } catch (e: unknown) {
      setStatus('idle');
      const msg =
        e instanceof Error && e.message
          ? e.message.replace(/^Error: /, '')
          : 'Something went wrong while publishing. Try again.';

      posthog?.capture('publishFailed', { courseId: course.id, message: msg });
      setError(msg);
    }
  };

  // Disable when: actively publishing, or no changes to ship.
  const disabled = status === 'publishing' || !changes;
  // Pulse only when there's something to publish AND the user can
  // actually publish it.
  const animated = !!changes && publishable && status === 'idle';

  const label =
    status === 'publishing'
      ? 'Publishing…'
      : status === 'success'
        ? 'Published'
        : 'Publish';

  const iconName =
    status === 'success' ? 'CIRCLE_Completed' : 'ACTIONS_Publish';

  return (
    <>
      <span css={{ position: 'relative' }}>
        <Wrapper>
          <ButtonStyled
            withIcon
            css={[
              animated &&
                css`
                  &:not([disabled]) {
                    box-shadow:
                      0 0 0 0 ${toColor(theme.colors.clickable, 0.3)},
                      0 0 0 0 ${toColor(theme.colors.clickable, 0.3)};
                    animation: ${pulse} 1.5s infinite;
                    &:hover {
                      animation: none;
                    }
                  }
                `,
              iconOnlyOnMobileCss,
            ]}
            disabled={disabled}
            onClick={handleClick}
          >
            <Icon name={iconName} size="25" tooltip={label} />
            <span>{label}</span>
          </ButtonStyled>
        </Wrapper>
        {status === 'success' && (
          <FlashStyled tone="success">
            <Icon name="CIRCLE_Completed" size="14" />
            Your changes are live.
          </FlashStyled>
        )}
        {error && <FlashStyled tone="danger">{error}</FlashStyled>}
      </span>

      {confirming && (
        <ModalBackdropStyled onClick={() => setConfirming(false)}>
          <ModalCardStyled onClick={e => e.stopPropagation()}>
            <h3>Publish these changes?</h3>
            <p>
              Guests who check in will see the new version right away. You can
              keep editing and publish again anytime.
            </p>
            <div className="actions">
              <button
                css={t => ({
                  background: t.colors.white,
                  border: `1px solid ${t.colors.border}`,
                  color: t.colors.grayDark,
                })}
                type="button"
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
              <button
                css={t => ({
                  background: t.colors.clickable,
                  border: `1px solid ${t.colors.clickable}`,
                  color: t.colors.white,
                })}
                type="button"
                onClick={() => void doPublish()}
              >
                Publish
              </button>
            </div>
          </ModalCardStyled>
        </ModalBackdropStyled>
      )}
    </>
  );
}

export default PublishBtn;
